<script setup lang="ts">
const { t: $t } = useI18n()

import type { ShareWithCustomerPayload } from './Page.vue'
import { FileFolderType } from '~/prisma/enums'

const props = defineProps<{
  payload: ShareWithCustomerPayload
}>()
const emit = defineEmits<{ (e: 'close'): void }>()

const { $trpc, queryClient, useMutation, makeTrpcErrorToast } = useMutationHelpers()
const notification = useNotification()

const shareFolder = useMutation({
  mutationFn: $trpc.fileFolder.shareFolderWithCustomer.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('fileFolder.share.error') }),
  onSuccess: async () => {
    await Promise.all(getFileFolderQueryInvalidations(FileFolderType.Offer, queryClient))
    notification.success({ title: $t('fileFolder.share.success'), duration: 4500 })
    emit('close')
  },
})

const withdrawShareFolder = useMutation({
  mutationFn: $trpc.fileFolder.withdrawShareFolderWithCustomer.mutate,
  onError: makeTrpcErrorToast(notification, { description: $t('fileFolder.withdraw.error') }),
  onSuccess: async () => {
    await Promise.all(getFileFolderQueryInvalidations(FileFolderType.Offer, queryClient))
    notification.success({ title: $t('fileFolder.withdraw.success'), duration: 4500 })
    emit('close')
  },
})

const isPending = computed(() => shareFolder.isPending.value || withdrawShareFolder.isPending.value)

function submitHandler() {
  if (props.payload.mode === 'share') {
    shareFolder.mutate({ id: props.payload.folderId })
  } else {
    withdrawShareFolder.mutate({ id: props.payload.folderId })
  }
}
</script>

<template>
  <ThePopup
    :title="$t(`fileFolder.modal.${payload.mode}.title`)"
    show
    @close="emit('close')"
  >
    <n-form @submit.prevent="submitHandler">
      <div class="mb-4 font-semibold">
        {{ $t(`fileFolder.modal.${payload.mode}.description`) }}
      </div>
      <div class="flex justify-end gap-2">
        <n-button quaternary :disabled="isPending" @click="emit('close')">
          {{ $t('common.no') }}
        </n-button>
        <n-button type="primary" :disabled="isPending" :loading="isPending" attr-type="submit">
          {{ $t('common.yes') }}
        </n-button>
      </div>
    </n-form>
  </ThePopup>
</template>
